import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchPackages,
  fetchPackage,
  createPackage,
  updatePackage,
  deletePackage,
  type ListPackagesQuery,
  type CreatePackagePayload,
  type UpdatePackagePayload,
} from '@/services/api/packages.api';

export function usePackages(query: ListPackagesQuery, enabled = true) {
  return useQuery({
    queryKey: ['packages', query],
    queryFn: () => fetchPackages(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function usePackage(id: string | null) {
  return useQuery({
    queryKey: ['packages', id, 'detail'],
    queryFn: () => fetchPackage(id as string),
    staleTime: 30_000,
    enabled: id !== null,
  });
}

export function useCreatePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePackagePayload) => createPackage(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
  });
}

export function useUpdatePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePackagePayload }) =>
      updatePackage(id, payload),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['packages', id, 'detail'] });
      void queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
  });
}

export function useDeletePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePackage(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
  });
}
